"use client";

import { useAuth, useClerk, useOrganization } from "@clerk/nextjs";
import { useEffect, useRef } from "react";
import { useSearchParams } from "next/navigation";
import { logError } from "~/lib/logger";
import {
  persistMarketingBrand,
  resolveClientMarketingBrand,
  type MarketingBrandId,
} from "~/lib/marketing-brand";

const PROVISIONING_LOCK_KEY = "egpt-marketing-portal-provisioning";

function portalEndpoint(brand: MarketingBrandId): string {
  return brand === "siteglue"
    ? "/api/clerk/ensure-siteglue-portal"
    : "/api/clerk/ensure-inbound-gpt-portal";
}

/**
 * Ensures marketing / hire signups join the correct Clerk org:
 * - brand=siteglue → SiteGlue AI org
 * - default → Inbound GPT portal org
 */
export function EnsureMarketingPortalAccess() {
  const { isLoaded: authLoaded, isSignedIn, userId } = useAuth();
  const { organization, isLoaded: orgLoaded } = useOrganization();
  const { setActive } = useClerk();
  const searchParams = useSearchParams();
  const started = useRef(false);

  useEffect(() => {
    const brand = resolveClientMarketingBrand(searchParams);
    persistMarketingBrand(brand);
  }, [searchParams]);

  useEffect(() => {
    if (organization?.id && typeof window !== "undefined") {
      try {
        sessionStorage.removeItem(PROVISIONING_LOCK_KEY);
      } catch {
        /* ignore */
      }
    }
  }, [organization?.id]);

  useEffect(() => {
    if (!authLoaded || !orgLoaded || !isSignedIn || !userId || started.current) {
      return;
    }
    if (organization?.id) return;

    const brand = resolveClientMarketingBrand(searchParams);
    const lockValue = `${brand}:${userId}`;

    if (typeof window !== "undefined") {
      try {
        if (sessionStorage.getItem(PROVISIONING_LOCK_KEY) === lockValue) return;
        sessionStorage.setItem(PROVISIONING_LOCK_KEY, lockValue);
      } catch {
        /* ignore */
      }
    }

    started.current = true;

    void (async () => {
      try {
        const res = await fetch(portalEndpoint(brand), { method: "POST" });
        if (!res.ok) {
          const body = (await res.json().catch(() => ({}))) as { error?: string };
          throw new Error(body.error ?? "Could not join organization");
        }

        const { organizationId } = (await res.json()) as { organizationId?: string };
        if (organizationId && setActive) {
          await setActive({ organization: organizationId });
        }
      } catch (error) {
        logError("EnsureMarketingPortalAccess failed", error);
        started.current = false;
        if (typeof window !== "undefined") {
          try {
            sessionStorage.removeItem(PROVISIONING_LOCK_KEY);
          } catch {
            /* ignore */
          }
        }
      }
    })();
  }, [authLoaded, orgLoaded, isSignedIn, userId, organization?.id, setActive, searchParams]);

  return null;
}

/** @deprecated Prefer EnsureMarketingPortalAccess */
export function EnsureInboundGptPortalAccess() {
  return <EnsureMarketingPortalAccess />;
}
